import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import UpfrontRentsRow from './UpfrontRentsRow';

const Tbody = ({ upfrontRents, t }) =>
  upfrontRents.map(upfrontRent => (
    <UpfrontRentsRow key={upfrontRent.id} t={t} {...upfrontRent} />
  ));

const UpfrontRentsTable = ({
  className,
  upfrontRents,
  count,
  page,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { t } = useTranslation();
  const minWidth = '9rem';

  const theads = useMemo(
    () => () => (
      <TableRow
        sx={{
          '& .MuiTableCell-head': { whiteSpace: 'nowrap', textAlign: 'center' }
        }}
      >
        <TableCell variant="head" key="client" style={{ minWidth }}>
          {t('COMMON.CLIENT')}
        </TableCell>
        <TableCell variant="head" key="equipment" style={{ minWidth }}>
          {t('CONTRACT_VIEW.FOLIO')}
        </TableCell>
        <TableCell variant="head" key="signature-date" style={{ minWidth }}>
          {t('COMMON.DESCRIPTION')}
        </TableCell>
        <TableCell variant="head" key="first-rent" style={{ minWidth }}>
          {t('COMMON.BILLING_MONTH')}
        </TableCell>
        <TableCell variant="head" key="last-rent" style={{ minWidth }}>
          {t('COMMON.RENT_MONTH')}
        </TableCell>
        <TableCell variant="head" key="gross-investment" style={{ minWidth }}>
          {t('COMMON.MONTH_DIFFERENCE')}
        </TableCell>
        <TableCell variant="head" key="initial-payment" style={{ minWidth }}>
          {t('COMMON.RENT')}
        </TableCell>
        <TableCell variant="head" key="net-investment" style={{ minWidth }}>
          {t('COMMON.IVA')}
        </TableCell>
        <TableCell variant="head" key="net-investment" style={{ minWidth }}>
          {t('COMMON.TOTAL_IVA_INCLUDED')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(() => () => <Tbody t={t} upfrontRents={upfrontRents} />, [
    upfrontRents,
    t
  ]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, upfrontRents]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

UpfrontRentsTable.propTypes = {
  className: PropTypes.string,
  upfrontRents: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool
};

UpfrontRentsTable.defaultProps = {
  count: 0
};

export default UpfrontRentsTable;
